'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  CalendarDays,
  AlertCircle,
  Users,
  Megaphone,
  MessageSquare,
  Sun,
  ChevronLeft,
  ChevronRight,
  Settings,
  BookOpen,
  Building2,
  ChevronDown,
  LogOut,
  Menu,
  X,
  BarChart2,
  Bell,
} from 'lucide-react';
import { useTeam, TEAMS, TeamId } from '@/context/TeamContext';
import { useAuth } from '@/contexts/AuthContext';

import { useRoomUnreadCounts } from '@/hooks/useRoomMessages';
import { useNeedsAttention } from '@/hooks/useNeedsAttention';
import PresenceSelector from '@/components/PresenceSelector';


interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ElementType;
  badgeVariant?: 'alert' | 'neutral';
  dynamicBadge?: 'rooms' | 'needs-attention';
  badge?: number;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'nav-today', label: 'Today', href: '/today', icon: Sun },
  { id: 'nav-announcements', label: 'Announcements', href: '/announcements', icon: Bell },
  { id: 'nav-schedule', label: 'Schedule', href: '/schedule', icon: CalendarDays },
  {
    id: 'nav-needs-attention',
    label: 'Needs Attention',
    href: '/needs-attention',
    icon: AlertCircle,
    badgeVariant: 'alert',
    dynamicBadge: 'needs-attention',
  },
  {
    id: 'nav-rooms',
    label: 'Rooms',
    href: '/rooms',
    icon: MessageSquare,
    badgeVariant: 'neutral',
    dynamicBadge: 'rooms',
  },
  { id: 'nav-outreach', label: 'Outreach', href: '/outreach', icon: Megaphone },
  { id: 'nav-team', label: 'Team', href: '/team', icon: Users },
  { id: 'nav-reporting', label: 'Reporting', href: '/reporting', icon: BarChart2 },
  { id: 'nav-directory', label: 'Directory', href: '/directory', icon: BookOpen },
  { id: 'nav-company', label: 'Company', href: '/company', icon: Building2 },
  { id: 'nav-settings', label: 'Settings', href: '/settings', icon: Settings },
];

const TEAM_ACCENT_CLASSES: Record<TeamId, { dot: string; activeBg: string; activeText: string }> = {
  education:   { dot: 'bg-teal-500',   activeBg: 'bg-teal-50',   activeText: 'text-teal-700' },
  production:  { dot: 'bg-indigo-500', activeBg: 'bg-indigo-50', activeText: 'text-indigo-700' },
  development: { dot: 'bg-amber-500',  activeBg: 'bg-amber-50',  activeText: 'text-amber-700' },
  leadership:  { dot: 'bg-violet-500', activeBg: 'bg-violet-50', activeText: 'text-violet-700' },
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export default function Sidebar() {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [teamMenuOpen, setTeamMenuOpen] = useState(false);
  const { activeTeam, setActiveTeamId, canSwitchTeams } = useTeam();
  const { userProfile, signOut } = useAuth();

  // Live badge counts
  const { unreadCounts } = useRoomUnreadCounts();
  const { items: needsAttentionItems } = useNeedsAttention();

  const totalUnreadRooms = Object.values(unreadCounts).reduce((sum, n) => sum + n, 0);
  const needsAttentionCount = needsAttentionItems.length;

  const accent = TEAM_ACCENT_CLASSES[activeTeam.id];

  const displayName = userProfile?.fullName || 'CFVA Staff';
  const displayTitle = userProfile?.role
    ? userProfile.role.charAt(0).toUpperCase() + userProfile.role.slice(1)
    : '';
  const initials = getInitials(displayName);

  function getBadgeCount(item: NavItem): number | undefined {
    if (item.dynamicBadge === 'rooms') return totalUnreadRooms > 0 ? totalUnreadRooms : undefined;
    if (item.dynamicBadge === 'needs-attention') return needsAttentionCount > 0 ? needsAttentionCount : undefined;
    return item.badge;
  }

  function handleNavClick() {
    setMobileOpen(false);
  }

  const sidebarContent = (isMobile = false) => (
    <aside
      className={`
        relative flex flex-col h-full bg-white dark:bg-slate-900 border-r border-stone-100 dark:border-slate-800 shrink-0
        transition-all duration-300 ease-smooth
        ${isMobile ? 'w-64' : collapsed ? 'w-16' : 'w-56'}
      `}
      aria-label="Main navigation"
    >
      {/* Logo */}
      <div className={`flex items-center h-14 px-3 border-b border-stone-100 dark:border-slate-800 ${(!isMobile && collapsed) ? 'justify-center' : 'gap-2.5'}`}>
        <AppLogo size={28} />
        {(isMobile || !collapsed) && (
          <span className="font-semibold text-[15px] text-slate-800 dark:text-slate-100 tracking-tight whitespace-nowrap overflow-hidden">
            CFVA Hub
          </span>
        )}
        {isMobile && (
          <button
            onClick={() => setMobileOpen(false)}
            className="ml-auto p-1.5 rounded-lg text-slate-400 hover:bg-stone-100 transition-colors"
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Team Context */}
      {(isMobile || !collapsed) && (
        <div className="px-2 pt-3 pb-1">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest px-2 mb-1.5">Team Context</p>

          {canSwitchTeams ? (
            <div className="relative">
              <button
                onClick={() => setTeamMenuOpen(!teamMenuOpen)}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg border transition-all duration-150 ${accent.activeBg} border-transparent hover:border-stone-200 dark:hover:border-slate-700`}
              >
                <span className={`w-2 h-2 rounded-full shrink-0 ${accent.dot}`} />
                <div className="flex-1 min-w-0 text-left">
                  <p className={`text-[13px] font-semibold leading-tight ${accent.activeText}`}>{activeTeam.label}</p>
                  <p className="text-[10px] text-slate-400 truncate leading-tight mt-0.5">{activeTeam.description}</p>
                </div>
                <ChevronDown
                  size={13}
                  className={`shrink-0 text-slate-400 transition-transform duration-150 ${teamMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {teamMenuOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-stone-200 dark:border-slate-700 rounded-xl shadow-lg z-50 overflow-hidden">
                  {TEAMS.map((team) => {
                    const ta = TEAM_ACCENT_CLASSES[team.id];
                    const isActive = team.id === activeTeam.id;
                    return (
                      <button
                        key={team.id}
                        onClick={() => { setActiveTeamId(team.id); setTeamMenuOpen(false); }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-stone-50 dark:hover:bg-slate-800 ${isActive ? ta.activeBg : ''}`}
                      >
                        <span className={`w-2 h-2 rounded-full shrink-0 ${ta.dot}`} />
                        <div className="min-w-0">
                          <p className={`text-[13px] font-semibold leading-tight ${isActive ? ta.activeText : 'text-slate-700'}`}>{team.label}</p>
                          <p className="text-[10px] text-slate-400 truncate">{team.description}</p>
                        </div>
                        {isActive && (
                          <span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full ${ta.activeBg} ${ta.activeText}`}>
                            Active
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg ${accent.activeBg}`}>
              <span className={`w-2 h-2 rounded-full shrink-0 ${accent.dot}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-[13px] font-semibold leading-tight ${accent.activeText}`}>{activeTeam.label}</p>
                <p className="text-[10px] text-slate-400 truncate leading-tight mt-0.5">{activeTeam.description}</p>
              </div>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-white/60 ${accent.activeText}`}>
                Locked
              </span>
            </div>
          )}
        </div>
      )}

      {/* Collapsed team dot indicator */}
      {!isMobile && collapsed && (
        <div className="flex justify-center pt-3 pb-1">
          <div
            title={`Team: ${activeTeam.label}`}
            className={`w-3 h-3 rounded-full ${accent.dot}`}
          />
        </div>
      )}

      {/* Divider */}
      {(isMobile || !collapsed) && <div className="mx-4 mt-2 mb-1 border-t border-stone-100 dark:border-slate-800" />}

      {/* Nav */}
      <nav className="flex-1 py-2 px-2 space-y-0.5 overflow-y-auto scrollbar-thin">
        {NAV_ITEMS.map((item) => {
          const NavIcon = item.icon;
          const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
          const badgeCount = getBadgeCount(item);
          const isIconOnly = !isMobile && collapsed;

          return (
            <Link
              key={item.id}
              href={item.href}
              title={isIconOnly ? item.label : undefined}
              onClick={handleNavClick}
              className={`
                group flex items-center gap-3 px-2.5 py-2 rounded-lg text-[13.5px] font-medium
                transition-all duration-150
                ${isActive
                  ? `${accent.activeBg} ${accent.activeText}`
                  : 'text-slate-500 dark:text-slate-400 hover:bg-stone-50 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200'
                }
                ${isIconOnly ? 'justify-center' : ''}
              `}
            >
              <NavIcon
                size={17}
                className={`shrink-0 transition-colors duration-150 ${isActive ? accent.activeText : 'text-slate-400 group-hover:text-slate-600'}`}
              />
              {!isIconOnly && (
                <>
                  <span className="flex-1 truncate">{item.label}</span>
                  {badgeCount !== undefined && (
                    <span
                      className={`
                        inline-flex items-center justify-center min-w-[18px] h-[18px] px-1
                        rounded-full text-[10px] font-semibold tabular-nums
                        ${item.badgeVariant === 'alert' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}
                      `}
                    >
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                </>
              )}
              {isIconOnly && badgeCount !== undefined && (
                <span
                  className={`
                    absolute left-8 top-1 inline-flex items-center justify-center
                    min-w-[14px] h-[14px] px-0.5 rounded-full text-[9px] font-bold
                    ${item.badgeVariant === 'alert' ? 'bg-red-500 text-white' : 'bg-slate-400 text-white'}
                  `}
                >
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="px-2 py-3 border-t border-stone-100 dark:border-slate-800 space-y-0.5">
        <Link
          href="/settings"
          title={(!isMobile && collapsed) ? 'Settings' : undefined}
          onClick={handleNavClick}
          className={`
            flex items-center gap-3 px-2.5 py-2 rounded-lg text-[13.5px] font-medium
            text-slate-400 dark:text-slate-500 hover:bg-stone-50 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300 transition-all duration-150
            ${(!isMobile && collapsed) ? 'justify-center' : ''}
          `}
        >
          <Settings size={17} className="shrink-0 text-slate-400 dark:text-slate-500" />
          {(isMobile || !collapsed) && <span>Settings</span>}
        </Link>

        {(isMobile || !collapsed) && (
          <div className="flex items-center gap-2.5 px-2.5 py-2 mt-1">
            <div className="w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
              <span className="text-[11px] font-bold text-slate-600 dark:text-slate-300">{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold text-slate-700 dark:text-slate-200 truncate">{displayName}</p>
              {displayTitle && <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate capitalize">{displayTitle}</p>}
              {userProfile?.id && (
                <PresenceSelector userId={userProfile.id} collapsed={false} />
              )}
            </div>
            <button
              onClick={signOut}
              title="Sign out"
              className="p-1 rounded-md text-slate-300 hover:text-slate-600 hover:bg-stone-100 transition-colors"
            >
              <LogOut size={14} />
            </button>
          </div>
        )}
        {!isMobile && collapsed && userProfile?.id && (
          <div className="flex justify-center py-1">
            <PresenceSelector userId={userProfile.id} collapsed={true} />
          </div>
        )}
      </div>

      {/* Collapse toggle — desktop only */}
      {!isMobile && (
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-[72px] w-6 h-6 bg-white dark:bg-slate-900 border border-stone-200 dark:border-slate-700 rounded-full flex items-center justify-center shadow-sm hover:bg-stone-50 dark:hover:bg-slate-800 transition-colors z-10"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={12} className="text-slate-400" /> : <ChevronLeft size={12} className="text-slate-400" />}
        </button>
      )}
    </aside>
  );

  return (
    <>
      {/* Mobile hamburger button — shown in top bar via AppLayout */}
      <button
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-3 left-3 z-40 w-8 h-8 bg-white dark:bg-slate-900 border border-stone-200 dark:border-slate-700 rounded-lg flex items-center justify-center shadow-sm hover:bg-stone-50 dark:hover:bg-slate-800 transition-colors"
        aria-label="Open navigation"
      >
        <Menu size={16} className="text-slate-600 dark:text-slate-300" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`
          md:hidden fixed inset-y-0 left-0 z-50 h-full transition-transform duration-300
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {sidebarContent(true)}
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex h-screen">
        {sidebarContent(false)}
      </div>
    </>
  );
}